import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect } from 'react';
import { useNavigate, useParams, useRouterState } from '@tanstack/react-router';
import { Link } from '@tanstack/react-router';
import Layout from '@/components/Layout';
import { apiFetch } from '@/utils/api';
import { formatDate } from '@/utils/formatters';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
    FunnelIcon,
    XMarkIcon,
    TruckIcon,
    ArchiveBoxArrowDownIcon
} from '@heroicons/react/24/outline';

interface InventoryTransaction {
    id: string;
    product: {
        id: string;
        name: string;
        commodity_code?: string;
    };
    quantity: number;
    unit_cost?: number;
    expiry_date?: string;
    status: string;
    notes?: string;
}

interface InventoryBatch {
    id: string;
    batch_number?: string;
    // Delivery note fields
    document_number?: string;
    supplier?: {
        id: string;
        name: string;
    };
    delivery_date?: string;
    status: string;
    // Location (optional for batches without assigned location)
    cost_center?: {
        id: string;
        name: string;
    };
    supplier_invoice?: {
        id: string;
        invoice_number: string;
        vendor: {
            id: string;
            name: string;
        };
    };
    shipment_reference?: string;
    received_date?: string;
    expiration_date?: string;
    is_active: boolean;
    created_at: string;
    // Transactions containing the actual products
    transactions?: InventoryTransaction[];
}

interface Product {
    id: string;
    name: string;
    commodity_code?: string;
}

interface CostCenter {
    id: string;
    name: string;
}

interface Filters {
    product_id?: string;
    cost_center_id?: string;
    active_only: boolean;
    search: string;
}

export default function InventoryBatchesPage() {
    const navigate = useNavigate();
  const params = useParams({ strict: false });
  const routerState = useRouterState();
    const [batches, setBatches] = useState<InventoryBatch[]>([]);
    const [products, setProducts] = useState<Product[]>([]);
    const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [filters, setFilters] = useState<Filters>({
        active_only: true,
        search: ''
    });
    const [showFilters, setShowFilters] = useState(false);

    const handleBatchClick = (batchId: string) => {
        navigate({ to: `/inventory/batches/${batchId}`});
    };

    useEffect(() => {
        fetchBatches();
        fetchProducts();
        fetchCostCenters();
    }, [filters]);

    const fetchBatches = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();

            if (filters.product_id) params.append('product_id', filters.product_id);
            if (filters.cost_center_id) params.append('cost_center_id', filters.cost_center_id);
            params.append('active_only', filters.active_only.toString());
            params.append('limit', '100');

            const response = await apiFetch(`/api/inventory/batches/?${params}`);
            if (response.ok) {
                const data = await response.json();
                setBatches(data);
            } else {
                setError('Failed to fetch batches');
            }
        } catch (err) {
            setError('Network error while fetching batches');
            console.error('Error fetching batches:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchProducts = async () => {
        try {
            const response = await apiFetch('/api/products/?limit=100');
            if (response.ok) {
                const data = await response.json();
                setProducts(data);
            }
        } catch (err) {
            console.error('Error fetching products:', err);
        }
    };

    const fetchCostCenters = async () => {
        try {
            const response = await apiFetch('/api/cost-centers/');
            if (response.ok) {
                const data = await response.json();
                setCostCenters(data);
            }
        } catch (err) {
            console.error('Error fetching cost centers:', err);
        }
    };

    const handleFilterChange = (key: keyof Filters, value: any) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const clearFilters = () => {
        setFilters({
            active_only: true,
            search: ''
        });
    };

    const filteredBatches = batches.filter(batch => {
        if (filters.search) {
            const searchLower = filters.search.toLowerCase();
            return (
                // Search in transaction products
                batch.transactions?.some(t => t.product.name.toLowerCase().includes(searchLower)) ||
                batch.batch_number?.toLowerCase().includes(searchLower) ||
                batch.document_number?.toLowerCase().includes(searchLower) ||
                batch.shipment_reference?.toLowerCase().includes(searchLower) ||
                batch.supplier?.name.toLowerCase().includes(searchLower) ||
                batch.supplier_invoice?.vendor.name.toLowerCase().includes(searchLower)
            );
        }
        return true;
    });

    const hasActiveFilters = filters.product_id || filters.cost_center_id || !filters.active_only || filters.search;

    return (
        <Layout>
            <div className="space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight">Inventory Batches</h1>
                        <p className="text-muted-foreground">
                            Manage inventory batches and track shipments from vendors
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            <FunnelIcon className="h-4 w-4 mr-2" />
                            Filters
                            {hasActiveFilters && (
                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    Active
                                </span>
                            )}
                        </button>
                        <Link
                            to={"/inventory/batches/receive-shipment" as any}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            <TruckIcon className="h-4 w-4 mr-2" />
                            Receive Shipment
                        </Link>
                    </div>
                </div>

                {/* Filters Panel */}
                {showFilters && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Product
                                </label>
                                <select
                                    value={filters.product_id || ''}
                                    onChange={(e) => handleFilterChange('product_id', e.target.value || undefined)}
                                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                >
                                    <option value="">All Products</option>
                                    {products.map(product => (
                                        <option key={product.id} value={product.id}>
                                            {product.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Location
                                </label>
                                <select
                                    value={filters.cost_center_id || ''}
                                    onChange={(e) => handleFilterChange('cost_center_id', e.target.value || undefined)}
                                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                >
                                    <option value="">All Locations</option>
                                    {costCenters.map(center => (
                                        <option key={center.id} value={center.id}>
                                            {center.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Status
                                </label>
                                <select
                                    value={filters.active_only.toString()}
                                    onChange={(e) => handleFilterChange('active_only', e.target.value === 'true')}
                                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                >
                                    <option value="true">Active Only</option>
                                    <option value="false">All Batches</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Search
                                </label>
                                <input
                                    type="text"
                                    value={filters.search}
                                    onChange={(e) => handleFilterChange('search', e.target.value)}
                                    placeholder="Search batches..."
                                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                />
                            </div>
                        </div>
                        {hasActiveFilters && (
                            <div className="mt-4 flex justify-end">
                                <button
                                    onClick={clearFilters}
                                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                >
                                    <XMarkIcon className="h-4 w-4 mr-1" />
                                    Clear Filters
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* Batches Table */}
                <div className="bg-white">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Document</TableHead>
                                <TableHead>Products</TableHead>
                                <TableHead>Location</TableHead>
                                <TableHead>Supplier</TableHead>
                                <TableHead>Date</TableHead>
                                <TableHead>Status</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={7} className="text-center py-8">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                                        <p className="mt-2 text-sm text-gray-500">Loading batches...</p>
                                    </TableCell>
                                </TableRow>
                            ) : error ? (
                                <TableRow>
                                    <TableCell colSpan={7} className="text-center py-8">
                                        <p className="text-red-600">{error}</p>
                                        <button
                                            onClick={fetchBatches}
                                            className="mt-2 text-blue-600 hover:text-blue-500"
                                        >
                                            Try again
                                        </button>
                                    </TableCell>
                                </TableRow>
                            ) : filteredBatches.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} className="text-center py-8">
                                        <ArchiveBoxArrowDownIcon className="mx-auto h-12 w-12 text-gray-400" />
                                        <h3 className="mt-2 text-sm font-medium text-gray-900">No batches found</h3>
                                        <p className="mt-1 text-sm text-gray-500">
                                            {hasActiveFilters ? 'Try adjusting your filters.' : 'Get started by receiving your first shipment.'}
                                        </p>
                                        {!hasActiveFilters && (
                                            <div className="mt-6">
                                                <Link
                                                    to={"/inventory/batches/receive-shipment" as any}
                                                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                                                >
                                                    <TruckIcon className="h-4 w-4 mr-2" />
                                                    Receive Shipment
                                                </Link>
                                            </div>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredBatches.map((batch) => (
                                    <TableRow
                                        key={batch.id}
                                        onClick={() => handleBatchClick(batch.id)}
                                        className="cursor-pointer hover:bg-muted/50"
                                    >
                                        <TableCell>
                                            <div className="font-medium">
                                                {batch.document_number || batch.batch_number || `Delivery Note ${batch.id.slice(0, 8)}`}
                                            </div>
                                            {batch.shipment_reference && (
                                                <div className="text-sm text-muted-foreground">
                                                    Shipment: {batch.shipment_reference}
                                                </div>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {batch.transactions && batch.transactions.length > 0 ? (
                                                <div>
                                                    <div className="font-medium">
                                                        {batch.transactions.length} product{batch.transactions.length !== 1 ? 's' : ''}
                                                    </div>
                                                    <div className="text-sm text-muted-foreground">
                                                        {batch.transactions.slice(0, 2).map(t => t.product.name).join(', ')}
                                                        {batch.transactions.length > 2 && ` +${batch.transactions.length - 2} more`}
                                                    </div>
                                                </div>
                                            ) : (
                                                <span className="text-muted-foreground italic">No products</span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {batch.cost_center?.name || (
                                                <span className="text-muted-foreground italic">Not assigned</span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {batch.supplier?.name || (
                                                <span className="text-muted-foreground italic">Unknown</span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {formatDate(
                                                batch.delivery_date ||
                                                batch.received_date ||
                                                batch.created_at
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex flex-col gap-1">
                                                {batch.status && (
                                                    <Badge variant={
                                                        batch.status === 'CONFIRMED' ? 'default' :
                                                        batch.status === 'READY_FOR_REVIEW' ? 'secondary' :
                                                        batch.status === 'PROCESSING' ? 'secondary' :
                                                        'outline'
                                                    }>
                                                        {batch.status}
                                                    </Badge>
                                                )}
                                                {!batch.is_active && (
                                                    <Badge variant="destructive">
                                                        Inactive
                                                    </Badge>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>

            </div>
        </Layout>
    );
}

export const Route = createFileRoute('/inventory/batches/')({
  component: InventoryBatchesPage,
})
